import { Crown, MessagesSquare, MoonStar, SlidersHorizontal } from "lucide-react";
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ClientProfileClient } from "../api/client-profile";
import { getGameRuntimeConfig } from "../api/config";
import { ApiError } from "../api/http";
import { subscribeEmbeddedGameSession } from "../embed/game-embed";
import { openGamePortal, type GamePortalTarget } from "../forum-auth";
import { SessionStore, type SessionState } from "../session";
import { GG_PORTAL_LOGO_DATA_URL } from "./gg-logo-data";
import "./game-portal-launcher.css";

const BUTTON_SIZE = 54;
const EDGE_PADDING = 12;
const DEFAULT_BOTTOM_OFFSET = 92;
const DEFAULT_TOP_RATIO = 0.28;
const DRAG_THRESHOLD = 6;

type Position = { x: number; y: number };
type FanDirection = { x: -1 | 1; y: -1 | 1 };
type LauncherSession = Pick<SessionState, "authMode" | "token" | "isAnonymous">;

interface PortalMenuItem {
  label: string;
  target: GamePortalTarget;
  icon: typeof SlidersHorizontal;
}

const PORTAL_MENU_ITEMS: readonly PortalMenuItem[] = [
  { label: "模型切换", target: "models", icon: SlidersHorizontal },
  { label: "论坛", target: "forum", icon: MessagesSquare },
  { label: "会员权益", target: "membership", icon: Crown },
  { label: "月亮充值", target: "recharge", icon: MoonStar },
];

const FAN_VECTORS = [
  { x: 112, y: 11 },
  { x: 106, y: 50 },
  { x: 82, y: 82 },
  { x: 50, y: 106 },
] as const;

export interface GamePortalLauncherProps {
  session: LauncherSession;
  selectedModelId?: number;
  bottomOffset?: number;
  maxViewportWidth?: number;
  className?: string;
  onSession?: (session: SessionState) => void;
}

export function isFormalGameAccount(session: LauncherSession) {
  return session.authMode === "registered" && Boolean(session.token) && session.isAnonymous !== true;
}

export function GamePortalLauncher({
  session,
  selectedModelId,
  bottomOffset = DEFAULT_BOTTOM_OFFSET,
  maxViewportWidth,
  className,
  onSession,
}: GamePortalLauncherProps) {
  const isRegistered = isFormalGameAccount(session);
  const storageKey = useMemo(() => {
    try {
      return `apk-games:${getGameRuntimeConfig().packageId}:portal-launcher-position`;
    } catch {
      return "apk-games:portal-launcher-position";
    }
  }, []);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fan, setFan] = useState<FanDirection>({ x: -1, y: -1 });
  const [position, setPosition] = useState<Position>(() => getDefaultPosition(bottomOffset, maxViewportWidth));
  const positionRef = useRef(position);
  const sessionRef = useRef<SessionState>(SessionStore.normalize(session));
  const onSessionRef = useRef(onSession);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    suppressClick: false,
    pointerId: 0,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  sessionRef.current = SessionStore.normalize(session);
  onSessionRef.current = onSession;

  useEffect(() => {
    setMounted(true);
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<Position>;
      if (typeof saved.x !== "number" || typeof saved.y !== "number") return;
      updatePosition(clampPosition(saved.x, saved.y, bottomOffset, maxViewportWidth));
    } catch {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // UI 位置恢复失败不影响官网入口。
      }
    }
  }, [bottomOffset, maxViewportWidth, storageKey]);

  useEffect(() => subscribeEmbeddedGameSession((incoming) => {
    const commit = onSessionRef.current;
    if (!commit) return;
    const current = sessionRef.current;
    let next = current;

    if (incoming.authenticated && incoming.token) {
      const sameFormalAccount = current.authMode === "registered" && current.token === incoming.token;
      const base = sameFormalAccount ? current : SessionStore.logout(current);
      next = SessionStore.withLogin(
        base,
        incoming.token,
        undefined,
        incoming.user ?? (sameFormalAccount ? current.user : undefined),
        false,
        current.installId,
        current.fingerprintHash,
        sameFormalAccount ? current.membership : undefined,
      );
    } else if (current.authMode === "registered") {
      // 官网退出只清理正式账号；游戏已有匿名会话继续承担离线/降级叙事。
      next = SessionStore.logout(current);
    }

    if (next === current) return;
    sessionRef.current = next;
    commit(next);
  }), []);

  useEffect(() => {
    const token = session.token?.trim();
    if (!token) return;

    let active = true;
    void ClientProfileClient.getMe(token)
      .then((profile) => {
        if (!active) return;
        const current = sessionRef.current;
        if (current.token !== token) return;
        const next = SessionStore.normalize({
          ...current,
          user: profile.user,
          membership: profile.membership,
          lastError: undefined,
        });
        sessionRef.current = next;
        onSessionRef.current?.(next);
      })
      .catch((error: unknown) => {
        if (!active || !(error instanceof ApiError) || error.status !== 401) return;
        const current = sessionRef.current;
        if (current.token !== token) return;
        const next = {
          ...SessionStore.logout(current),
          lastError: current.authMode === "registered"
            ? "登录已过期，请重新登录"
            : "普通用户会话已过期，请重新连接",
        };
        sessionRef.current = next;
        onSessionRef.current?.(next);
      });

    return () => {
      active = false;
    };
  }, [session.token]);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      drag.moved = true;
      setDragging(true);
      updatePosition(clampPosition(drag.originX + dx, drag.originY + dy, bottomOffset, maxViewportWidth));
      event.preventDefault();
    };

    const finish = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      drag.active = false;
      setDragging(false);
      if (drag.moved) {
        drag.suppressClick = true;
        persistPosition(storageKey, positionRef.current);
      }
      if (buttonRef.current?.hasPointerCapture?.(event.pointerId)) {
        buttonRef.current.releasePointerCapture(event.pointerId);
      }
    };

    const resize = () => {
      updatePosition(clampPosition(positionRef.current.x, positionRef.current.y, bottomOffset, maxViewportWidth));
      setOpen(false);
    };

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize);
    };
  }, [bottomOffset, maxViewportWidth, storageKey]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  function updatePosition(next: Position) {
    positionRef.current = next;
    setPosition(next);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    setOpen(false);
    dragRef.current = {
      active: true,
      moved: false,
      suppressClick: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: positionRef.current.x,
      originY: positionRef.current.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function handleLauncherClick() {
    const drag = dragRef.current;
    if (drag.suppressClick) {
      drag.suppressClick = false;
      return;
    }
    setFan(resolveFanDirection(positionRef.current, maxViewportWidth));
    setOpen((current) => !current);
  }

  function openTarget(target: GamePortalTarget) {
    setOpen(false);
    openGamePortal(target === "models" ? { target, selectedModelId } : { target });
  }

  if (!mounted || typeof document === "undefined") return null;

  const rootClassName = [
    "agp-launcher",
    isRegistered ? "agp-launcher--registered" : "agp-launcher--anonymous",
    open ? "agp-launcher--open" : "",
    dragging ? "agp-launcher--dragging" : "",
    className ?? "",
  ].filter(Boolean).join(" ");

  return createPortal(
    <div ref={rootRef} className={rootClassName} style={{ left: position.x, top: position.y }}>
      {open ? (
        <div className="agp-launcher__menu" role="menu" aria-label="GG 官网快捷入口">
          {PORTAL_MENU_ITEMS.map((item, index) => {
            const Icon = item.icon;
            const vector = FAN_VECTORS[index];
            const itemStyle = {
              "--agp-x": `${vector.x * fan.x}px`,
              "--agp-y": `${vector.y * fan.y}px`,
              "--agp-delay": `${index * 28}ms`,
            } as CSSProperties;
            return (
              <button
                className={[
                  "agp-launcher__item",
                  `agp-launcher__item--fan-${fan.x < 0 ? "left" : "right"}`,
                  `agp-launcher__item--fan-${fan.y < 0 ? "up" : "down"}`,
                  index === PORTAL_MENU_ITEMS.length - 1 ? "agp-launcher__item--fan-end" : "",
                ].filter(Boolean).join(" ")}
                key={item.target}
                type="button"
                role="menuitem"
                style={itemStyle}
                onClick={() => openTarget(item.target)}
              >
                <Icon aria-hidden="true" />
                <span className="agp-launcher__label">{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      <button
        ref={buttonRef}
        className="agp-launcher__button"
        type="button"
        aria-label="打开 GG 官网快捷菜单"
        aria-expanded={open}
        aria-haspopup="menu"
        title={isRegistered ? "GG 官网服务（可拖动）" : "试玩账号与模型服务（可拖动）"}
        onPointerDown={handlePointerDown}
        onClick={handleLauncherClick}
      >
        <img className="agp-launcher__logo" src={GG_PORTAL_LOGO_DATA_URL} alt="" draggable={false} />
      </button>
    </div>,
    document.body,
  );
}

function getViewportSize() {
  if (typeof window === "undefined") return { width: 390, height: 844 };
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

function getHorizontalBounds(viewportWidth: number, maxViewportWidth?: number) {
  const contentWidth = maxViewportWidth ? Math.min(viewportWidth, maxViewportWidth) : viewportWidth;
  const left = Math.max(0, (viewportWidth - contentWidth) / 2);
  return { left, right: left + contentWidth };
}

function getDefaultPosition(bottomOffset: number, maxViewportWidth?: number): Position {
  const viewport = getViewportSize();
  const bounds = getHorizontalBounds(viewport.width, maxViewportWidth);
  return clampPosition(
    bounds.right - BUTTON_SIZE - EDGE_PADDING,
    viewport.height * DEFAULT_TOP_RATIO,
    bottomOffset,
    maxViewportWidth,
  );
}

function clampPosition(x: number, y: number, bottomOffset: number, maxViewportWidth?: number): Position {
  const viewport = getViewportSize();
  const bounds = getHorizontalBounds(viewport.width, maxViewportWidth);
  const minX = bounds.left + EDGE_PADDING;
  const maxX = Math.max(minX, bounds.right - BUTTON_SIZE - EDGE_PADDING);
  const minY = EDGE_PADDING;
  const maxY = Math.max(minY, viewport.height - BUTTON_SIZE - Math.max(EDGE_PADDING, bottomOffset));
  return {
    x: Math.min(Math.max(minX, x), maxX),
    y: Math.min(Math.max(minY, y), maxY),
  };
}

function resolveFanDirection(position: Position, maxViewportWidth?: number): FanDirection {
  const viewport = getViewportSize();
  const bounds = getHorizontalBounds(viewport.width, maxViewportWidth);
  const centerX = position.x + BUTTON_SIZE / 2;
  const centerY = position.y + BUTTON_SIZE / 2;
  return {
    x: centerX > (bounds.left + bounds.right) / 2 ? -1 : 1,
    y: centerY > viewport.height / 2 ? -1 : 1,
  };
}

function persistPosition(storageKey: string, position: Position) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(position));
  } catch {
    // UI 位置记忆失败不影响官网入口。
  }
}
